<template>
  <div class="page">
    <h2>排班管理</h2>
    <div class="toolbar">
      <select v-model.number="filters.therapistId">
        <option :value="null">所有理疗师</option>
        <option v-for="t in therapists" :key="t.id" :value="t.id">{{ t.name || t.id }}</option>
      </select>
      <select v-model.number="filters.activityId">
        <option :value="null">所有活动</option>
        <option v-for="a in activities" :key="a.id" :value="a.id">{{ a.name || a.id }}</option>
      </select>
      <input v-model="filters.startDate" type="date" />
      <input v-model="filters.endDate" type="date" />
      <select v-model="filters.status">
        <option value="">所有状态</option>
        <option v-for="option in statusFilterOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <button :disabled="loading" @click="loadData">查询</button>
      <button :disabled="loading" @click="openCreateDialog">新增时段</button>
      <button :disabled="loading" @click="showBatch = true">批量排班</button>
      <button :disabled="loading" @click="openBatchDeleteDialog">批量取消排班</button>
    </div>

    <section v-if="changeRequests.length" class="change-requests-panel">
      <h3>改班待审（{{ changeRequests.length }}）</h3>
      <table>
        <thead>
          <tr>
            <th>理疗师</th>
            <th>活动</th>
            <th>类型</th>
            <th>原班次</th>
            <th>申请改为</th>
            <th>原因</th>
            <th>提交时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="req in changeRequests" :key="req.id">
            <td>{{ req.therapistName }}</td>
            <td>{{ req.activityName }}</td>
            <td>{{ changeRequestTypeLabel(req.requestType) }}</td>
            <td>{{ req.originalScheduleDate }} {{ req.originalStartTime }} – {{ req.originalEndTime }}</td>
            <td>
              <span v-if="req.requestType === 'CANCEL'">取消班次</span>
              <span v-else>{{ req.newScheduleDate }} {{ req.newStartTime }} – {{ req.newEndTime }}</span>
            </td>
            <td>{{ req.reason || '—' }}</td>
            <td>{{ formatDateTime(req.createdAt) }}</td>
            <td>
              <button :disabled="loading" @click="approveChangeRequest(req)">通过</button>
              <button :disabled="loading" class="btn-danger" @click="rejectChangeRequest(req)">驳回</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <table>
      <thead>
        <tr><th>ID</th><th>理疗师</th><th>活动</th><th>日期</th><th>时间</th><th>状态</th><th>操作</th></tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.therapist?.name || item.therapist?.id }}</td>
          <td>{{ item.therapyActivity?.name || item.therapyActivity?.id }}</td>
          <td>{{ item.scheduleDate }}</td>
          <td>{{ item.startTime }} - {{ item.endTime }}</td>
          <td>
            <span :class="['status-badge', 'status-' + displayStatus(item).toLowerCase()]">
              {{ statusMap[displayStatus(item)] || displayStatus(item) }}
            </span>
          </td>
          <td>
            <button :disabled="loading" @click="remove(item.id)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="showCreate" class="dialog-mask">
      <div class="dialog">
        <h3>新增排班</h3>
        <div class="form-group">
          <label>理疗师</label>
          <select v-model.number="form.therapistId" @change="onCreateTherapistChange">
            <option :value="null">请选择理疗师</option>
            <option v-for="t in therapists" :key="t.id" :value="t.id">{{ t.name || t.id }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>活动</label>
          <select v-model.number="form.therapyActivityId" @change="onCreateActivityChange">
            <option :value="null">请选择活动</option>
            <option v-for="a in createFilteredActivities" :key="a.id" :value="a.id">{{ a.name || a.id }}</option>
          </select>
          <span v-if="form.therapistId && createLinkedActivityIds.length && createLinkedActivityIds.length < activities.length" class="filter-hint">仅显示该理疗师关联的活动</span>
          <span v-if="selectedCreateActivity" class="filter-hint">时长 {{ selectedCreateActivity.durationMinutes }} 分钟</span>
        </div>
        <div class="form-group">
          <label>日期</label>
          <input v-model="form.scheduleDate" type="date" />
        </div>
        <div class="form-row">
          <div class="form-group form-col">
            <label>开始时间</label>
            <input v-model="form.startTime" type="time" @change="onStartTimeChange" />
          </div>
          <div class="form-group form-col">
            <label>结束时间</label>
            <input v-model="form.endTime" type="time" :readonly="!!selectedCreateActivity" :class="{ 'readonly-input': selectedCreateActivity }" />
            <span v-if="selectedCreateActivity" class="filter-hint">根据活动时长自动计算</span>
          </div>
        </div>
        <div class="actions">
          <button :disabled="loading" @click="showCreate=false">取消</button>
          <button :disabled="loading" @click="createItem">保存</button>
        </div>
      </div>
    </div>

    <div v-if="showBatch" class="dialog-mask">
      <div class="dialog">
        <h3>批量排班</h3>
        <div class="form-group">
          <label>理疗师</label>
          <select v-model.number="batch.therapistId" @change="onBatchTherapistChange">
            <option :value="null">请选择理疗师</option>
            <option v-for="t in therapists" :key="t.id" :value="t.id">{{ t.name || t.id }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>活动</label>
          <select v-model.number="batch.activityId" @change="onBatchActivityChange">
            <option :value="null">请选择活动</option>
            <option v-for="a in batchFilteredActivities" :key="a.id" :value="a.id">{{ a.name || a.id }}</option>
          </select>
          <span v-if="batch.therapistId && batchLinkedActivityIds.length && batchLinkedActivityIds.length < activities.length" class="filter-hint">仅显示该理疗师关联的活动</span>
        </div>
        <div class="form-row">
          <div class="form-group form-col">
            <label>开始日期</label>
            <input v-model="batch.startDate" type="date" />
          </div>
          <div class="form-group form-col">
            <label>结束日期</label>
            <input v-model="batch.endDate" type="date" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group form-col">
            <label>工作开始</label>
            <input v-model="batch.workStart" type="time" />
          </div>
          <div class="form-group form-col">
            <label>工作结束</label>
            <input v-model="batch.workEnd" type="time" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group form-col">
            <label>午休开始（可选）</label>
            <input v-model="batch.breakStart" type="time" />
          </div>
          <div class="form-group form-col">
            <label>午休结束（可选）</label>
            <input v-model="batch.breakEnd" type="time" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group form-col">
            <label>晚餐开始（可选）</label>
            <input v-model="batch.dinnerStart" type="time" />
          </div>
          <div class="form-group form-col">
            <label>晚餐结束（可选）</label>
            <input v-model="batch.dinnerEnd" type="time" />
          </div>
        </div>
        <div class="actions">
          <button :disabled="loading" @click="showBatch=false">取消</button>
          <button :disabled="loading" @click="createBatch">执行</button>
        </div>
      </div>
    </div>

    <div v-if="showBatchDelete" class="dialog-mask">
      <div class="dialog">
        <h3>批量取消排班</h3>
        <p class="hint">按条件筛选排班记录进行批量删除，已预约的排班会自动跳过。</p>
        <select v-model.number="batchDelete.therapistId">
          <option :value="null">所有理疗师</option>
          <option v-for="t in therapists" :key="t.id" :value="t.id">{{ t.name || t.id }}</option>
        </select>
        <select v-model.number="batchDelete.activityId">
          <option :value="null">所有活动</option>
          <option v-for="a in activities" :key="a.id" :value="a.id">{{ a.name || a.id }}</option>
        </select>
        <input v-model="batchDelete.startDate" type="date" />
        <input v-model="batchDelete.endDate" type="date" />
        <div class="actions">
          <button :disabled="loading" @click="showBatchDelete=false">取消</button>
          <button :disabled="loading" class="btn-danger" @click="deleteBatch">确认删除</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { therapyScheduleService } from '@/services/therapyScheduleService'
import { therapistService } from '@/services/therapistService'
import { therapyActivityService } from '@/services/therapyActivityService'
import api from '@/services/api'

export default {
  name: 'TherapyScheduleManagement',
  data() {
    return {
      list: [],
      therapists: [],
      activities: [],
      form: { therapistId: null, therapyActivityId: null, scheduleDate: '', startTime: '09:00', endTime: '10:00', status: 'AVAILABLE' },
      batch: { therapistId: null, activityId: null, startDate: '', endDate: '', workStart: '09:00', workEnd: '18:00', breakStart: '12:00', breakEnd: '13:00', dinnerStart: '17:00', dinnerEnd: '18:00', intervalMinutes: 0 },
      batchDelete: { therapistId: null, activityId: null, startDate: '', endDate: '' },
      filters: { therapistId: null, activityId: null, startDate: '', endDate: '', status: '' },
      statusMap: { AVAILABLE: '可用', BOOKED: '已预约', LOCKED: '已锁定', COMPLETED: '已完成', CANCELLED: '已取消', EXPIRED: '过期', REST: '休息' },
      statusFilterOptions: [
        { value: 'AVAILABLE', label: '可用' },
        { value: 'BOOKED', label: '已预约' },
        { value: 'LOCKED', label: '已锁定' },
        { value: 'REST', label: '休息' },
        { value: 'EXPIRED', label: '过期' }
      ],
      showCreate: false,
      showBatch: false,
      showBatchDelete: false,
      loading: false,
      createLinkedActivityIds: [],
      createLinkedTherapistIds: [],
      batchLinkedActivityIds: [],
      batchLinkedTherapistIds: [],
      changeRequests: []
    }
  },
  computed: {
    selectedCreateActivity() {
      if (!this.form.therapyActivityId) return null
      return this.activities.find(a => a.id === this.form.therapyActivityId) || null
    },
    computedEndTime() {
      if (!this.selectedCreateActivity || !this.form.startTime) return null
      const [h, m] = this.form.startTime.split(':').map(Number)
      const totalMinutes = h * 60 + m + this.selectedCreateActivity.durationMinutes
      const newH = Math.floor(totalMinutes / 60)
      const newM = totalMinutes % 60
      return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`
    },
    createFilteredActivities() {
      if (!this.form.therapistId || this.createLinkedActivityIds.length === 0) return this.activities
      return this.activities.filter(a => this.createLinkedActivityIds.includes(a.id))
    },
    createFilteredTherapists() {
      if (!this.form.therapyActivityId || this.createLinkedTherapistIds.length === 0) return this.therapists
      return this.therapists.filter(t => this.createLinkedTherapistIds.includes(t.id))
    },
    batchFilteredActivities() {
      if (!this.batch.therapistId || this.batchLinkedActivityIds.length === 0) return this.activities
      return this.activities.filter(a => this.batchLinkedActivityIds.includes(a.id))
    }
  },
  mounted() {
    this.loadData()
    this.loadChangeRequests()
    this.loadOptions()
  },
  methods: {
    changeRequestTypeLabel(type) {
      if (type === 'CANCEL') return '取消'
      if (type === 'POSTPONE') return '延期'
      return type || '—'
    },
    formatDateTime(value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').slice(0, 16)
    },
    async loadChangeRequests() {
      try {
        const res = await therapyScheduleService.fetchChangeRequests({ status: 'PENDING' })
        this.changeRequests = res.data || []
      } catch (e) {
        console.error('加载改班申请失败', e)
        this.changeRequests = []
      }
    },
    async approveChangeRequest(req) {
      if (!window.confirm(`确认通过 ${req.therapistName} 的改班申请？`)) return
      this.loading = true
      try {
        await therapyScheduleService.approveChangeRequest(req.id)
        await Promise.all([this.loadChangeRequests(), this.loadData()])
      } catch (e) {
        alert(e?.response?.data?.error || e?.response?.data?.message || '审批失败')
      } finally {
        this.loading = false
      }
    },
    async rejectChangeRequest(req) {
      const reason = window.prompt('请输入驳回原因')
      if (reason == null) return
      const trimmed = reason.trim()
      if (!trimmed) {
        alert('请填写驳回原因')
        return
      }
      this.loading = true
      try {
        await therapyScheduleService.rejectChangeRequest(req.id, trimmed)
        await this.loadChangeRequests()
      } catch (e) {
        alert(e?.response?.data?.error || e?.response?.data?.message || '驳回失败')
      } finally {
        this.loading = false
      }
    },
    displayStatus(item) {
      return item?.displayStatus || item?.status || 'UNKNOWN'
    },
    localDateString(d = new Date()) {
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    openCreateDialog() {
      this.form = {
        therapistId: null,
        therapyActivityId: null,
        scheduleDate: this.localDateString(),
        startTime: '09:00',
        endTime: '10:00',
        status: 'AVAILABLE'
      }
      this.createLinkedActivityIds = []
      this.createLinkedTherapistIds = []
      this.showCreate = true
    },
    async onCreateTherapistChange() {
      this.form.therapyActivityId = null
      if (this.form.therapistId) {
        try {
          const res = await api.get(`/therapy/schedules/linked-activities?therapistId=${this.form.therapistId}`)
          this.createLinkedActivityIds = (res.data || []).map(a => a.id)
          this.createLinkedTherapistIds = []
        } catch (e) {
          this.createLinkedActivityIds = []
        }
      } else {
        this.createLinkedActivityIds = []
        this.createLinkedTherapistIds = []
      }
    },
    async onCreateActivityChange() {
      this.createLinkedTherapistIds = []
      if (this.form.therapyActivityId) {
        try {
          const res = await api.get(`/therapy/schedules/linked-therapists?activityId=${this.form.therapyActivityId}`)
          this.createLinkedTherapistIds = (res.data || []).map(t => t.id)
        } catch (e) {
          this.createLinkedTherapistIds = []
        }
      }
      if (this.computedEndTime) {
        this.form.endTime = this.computedEndTime
      }
    },
    onStartTimeChange() {
      if (this.computedEndTime) {
        this.form.endTime = this.computedEndTime
      }
    },
    async onBatchTherapistChange() {
      this.batch.activityId = null
      if (this.batch.therapistId) {
        try {
          const res = await api.get(`/therapy/schedules/linked-activities?therapistId=${this.batch.therapistId}`)
          this.batchLinkedActivityIds = (res.data || []).map(a => a.id)
          this.batchLinkedTherapistIds = []
        } catch (e) {
          this.batchLinkedActivityIds = []
        }
      } else {
        this.batchLinkedActivityIds = []
        this.batchLinkedTherapistIds = []
      }
    },
    async loadOptions() {
      try {
        const [therapistsRes, activitiesRes] = await Promise.all([
          therapistService.fetchList({ size: 1000, status: 'ACTIVE' }),
          therapyActivityService.fetchList({ size: 1000, status: 'PUBLISHED' })
        ])
        this.therapists = therapistsRes.data?.content || []
        this.activities = activitiesRes.data?.content || []
      } catch (e) {
        console.error('Failed to load options', e)
      }
    },
    async loadData() {
      this.loading = true
      try {
        const res = await therapyScheduleService.fetchList({ ...this.filters, size: 100 })
        this.list = res.data?.content || []
      } catch (e) {
        alert(e?.response?.data?.error || '加载排班失败')
      } finally {
        this.loading = false
      }
    },
    async createItem() {
      if (!this.form.therapistId || !this.form.therapyActivityId) {
        alert('请选择理疗师和活动')
        return
      }
      if (!this.form.scheduleDate) {
        alert('请选择排班日期')
        return
      }
      if (!this.form.startTime || !this.form.endTime) {
        alert('请填写开始时间与结束时间')
        return
      }
      this.loading = true
      try {
        await therapyScheduleService.create(this.form)
        this.showCreate = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '创建排班失败')
      } finally {
        this.loading = false
      }
    },
    async createBatch() {
      if (!this.batch.therapistId || !this.batch.activityId) {
        alert('请选择理疗师和活动')
        return
      }
      this.loading = true
      try {
        await therapyScheduleService.batchCreate(this.batch)
        this.showBatch = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '批量排班失败')
      } finally {
        this.loading = false
      }
    },
    async remove(id) {
      if (!window.confirm('确认删除该排班吗？')) return
      this.loading = true
      try {
        await therapyScheduleService.remove(id)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '删除排班失败')
      } finally {
        this.loading = false
      }
    },
    openBatchDeleteDialog() {
      this.batchDelete = {
        therapistId: this.filters.therapistId || null,
        activityId: this.filters.activityId || null,
        startDate: this.filters.startDate || this.localDateString(),
        endDate: this.filters.endDate || this.localDateString()
      }
      this.showBatchDelete = true
    },
    async deleteBatch() {
      if (!this.batchDelete.startDate || !this.batchDelete.endDate) {
        alert('请选择日期范围')
        return
      }
      const msg = `确认删除符合条件的排班吗？已预约的排班会自动跳过。`
      if (!window.confirm(msg)) return
      this.loading = true
      try {
        const res = await therapyScheduleService.batchDelete(this.batchDelete)
        const result = res.data?.data
        if (result) {
          alert(`操作完成：匹配 ${result.totalMatched} 条，删除 ${result.deletedCount} 条，跳过已预约 ${result.skippedCount} 条`)
        }
        this.showBatchDelete = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '批量取消排班失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.change-requests-panel {
  margin: 16px 0 24px;
  padding: 16px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
}
.change-requests-panel h3 {
  margin: 0 0 12px;
  font-size: 1rem;
  color: #92400e;
}
.btn-danger {
  color: #b91c1c;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
}
.form-group select,
.form-group input {
  height: 40px;
  line-height: 1.25;
}
.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 0.9375rem;
}
.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.form-col {
  flex: 1;
  min-width: 160px;
}
.toolbar select,
.toolbar input {
  height: 40px;
}
.toolbar select {
  min-width: 140px;
}
.filter-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 2px;
}
.status-badge {
  display: inline-block;
  min-width: 64px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  border: 1px solid transparent;
}

.status-available {
  color: #0f766e;
  background: #ccfbf1;
  border-color: #5eead4;
}

.status-booked {
  color: #1d4ed8;
  background: #dbeafe;
  border-color: #93c5fd;
}

.status-locked {
  color: #4b5563;
  background: #f3f4f6;
  border-color: #d1d5db;
}

.status-completed {
  color: #166534;
  background: #dcfce7;
  border-color: #86efac;
}

.status-cancelled {
  color: #b91c1c;
  background: #fee2e2;
  border-color: #fca5a5;
}

.status-expired {
  color: #92400e;
  background: #ffedd5;
  border-color: #fdba74;
}

.status-rest {
  color: #6d28d9;
  background: #ede9fe;
  border-color: #c4b5fd;
}

.status-unknown {
  color: #374151;
  background: #f3f4f6;
  border-color: #d1d5db;
}

.btn-danger {
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;
}
.btn-danger:hover {
  background: #b91c1c;
}
.btn-danger:disabled {
  background: #fca5a5;
  border-color: #fca5a5;
  cursor: not-allowed;
}

.hint {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 8px 0;
}
.readonly-input {
  background: #f3f4f6;
  cursor: not-allowed;
}
</style>